import React, { useState } from "react";
import { CardSize, CardVariant, ContentCard } from "components/ui";
import { CardItemType } from "components/ui/ContentCard";
import { ReactComponent as CloseIcon } from "Assets/icon/icon-close.svg";
import { Carousel } from "apps/user/learner/components/carousel";
import RecordingPic from "Assets/images/reacrdingThumbPH.jpg";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";
import { OriRecommendation } from "../types";
import styles from "./OriRecommendations.module.scss";

const LIST_NAME = "ori-chat-recommendations";

// Drives ContentCard's routing; unknown types fall back to a recording.
const TYPENAME_BY_CONTENT_TYPE: Record<string, string> = {
  recording: "LearnerRecording",
  video: "LearnerRecording",
  session: "LearnerGroupSession",
  article: "LearnerArticle",
};

function toCardItem(recommendation: OriRecommendation): CardItemType {
  const id = recommendation.id != null ? String(recommendation.id) : "";
  const contentType = recommendation.content_type ?? "recording";
  const typename = TYPENAME_BY_CONTENT_TYPE[contentType] ?? "LearnerRecording";
  const title = recommendation.title ?? recommendation.video_title ?? "";
  const rawDuration =
    recommendation.duration != null ? String(recommendation.duration) : "";
  const duration = /\d/.test(rawDuration) ? rawDuration : "";
  const theme = recommendation.primary_theme;

  return {
    id,
    __typename: typename,
    // The image variant collapses without a thumbnail (text overlays the image).
    imageUrl: recommendation.thumbnail_url || RecordingPic,
    title,
    subtitle:
      recommendation.practitioner ?? recommendation.practitioner_name ?? "",
    tags: theme ? [theme] : [],
    link:
      typename === "LearnerArticle"
        ? `/user/articles/${id}`
        : `/user/recordings/${id}`,
    duration,
  };
}

interface OriRecommendationsProps {
  recommendations: OriRecommendation[];
  // The Configuration this batch of recommendations was surfaced under - a
  // card impression/click/dismiss can happen before any conversation exists,
  // so no Thread/ConversationVersion dimensions are attached here.
  configurationId?: string | null;
  configurationName?: string | null;
}

const OriRecommendations: React.FC<OriRecommendationsProps> = ({
  recommendations,
  configurationId,
  configurationName,
}) => {
  const { t } = useTranslation(["ori"]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  if (!recommendations?.length) return null;

  const trackCardEvent = (name: string, id: string) => {
    trackEvent(name, {
      "Configuration ID": configurationId ?? null,
      "Configuration Name": configurationName ?? null,
      "Content ID": id,
    });
  };

  const items = recommendations.filter(
    (recommendation) =>
      recommendation.id != null && !dismissedIds.has(String(recommendation.id))
  );

  if (!items.length) return null;

  return (
    <div className={styles.wrapper} data-testid="ori-recommendations">
      <Carousel
        listName={LIST_NAME}
        items={items.map((recommendation, index) => {
          const id = String(recommendation.id);
          return (
            <ContentCard
              key={id}
              item={toCardItem(recommendation)}
              variant={CardVariant.IMAGE}
              size={CardSize.REGULAR}
              listName={LIST_NAME}
              listPosition={index + 1}
              onClickHandler={() =>
                trackCardEvent("Ori Recommendation Card Clicked", id)
              }
              onImpression={() =>
                trackCardEvent("Ori Recommendation Card Impression", id)
              }
              actionButtons={[
                {
                  icon: <CloseIcon />,
                  ariaLabel: t("ori:chat.card.dismissA11y"),
                  onClick: (_e, itemId) => {
                    trackCardEvent("Ori Recommendation Card Dismissed", itemId);
                    setDismissedIds((prev) => new Set(prev).add(itemId));
                  },
                },
              ]}
            />
          );
        })}
      />
    </div>
  );
};

export default OriRecommendations;
